import { useState } from 'react';
import type { EsmsPriceReference } from '../../types/esms';
import InfoTooltip from '../revenue/InfoTooltip';
import EsmsErrorBox from './EsmsErrorBox';

interface Props {
  data: EsmsPriceReference | null;
  isDark: boolean;
  isLoading: boolean;
  error: string | null;
}

function toRows(payload: any): Array<Record<string, any>> {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload === 'object') {
    const candidates = ['data', 'list', 'items', 'rows', 'PriceList', 'Prices'];
    for (const k of candidates) {
      if (Array.isArray(payload[k])) return payload[k];
    }
    return Object.entries(payload).map(([k, v]) => ({ key: k, value: typeof v === 'object' ? JSON.stringify(v) : v }));
  }
  return [{ value: String(payload) }];
}

function PriceTable({ rows, isDark }: { rows: Array<Record<string, any>>; isDark: boolean }) {
  if (rows.length === 0) {
    return <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>(rỗng)</div>;
  }
  const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className={isDark ? 'border-b border-white/10' : 'border-b border-gray-200'}>
            {cols.map((c) => (
              <th key={c} className={`text-left px-2 py-1 font-medium ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={isDark ? 'border-b border-white/5' : 'border-b border-gray-100'}>
              {cols.map((c) => {
                const v = r[c];
                const out = v === undefined || v === null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
                return (
                  <td key={c} className={`px-2 py-1 font-mono ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>{out}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EsmsPriceReferencePanel({ data, isDark, isLoading, error }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`backdrop-blur rounded-lg p-4 sm:p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center text-left ${isDark ? 'text-white' : 'text-gray-900'}`}
      >
        <span className="text-lg font-semibold">
          Bảng giá tham khảo
          <InfoTooltip isDark={isDark} text={'Bảng giá đơn vị SMS CSKH và SMS Quảng cáo theo nhà mạng.\nCache 24h.\n\nNguồn: eSMS GetSMSPrice + GetSMSPriceAds.'} />
        </span>
        <span className="flex-1" />
        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{open ? 'Thu gọn' : 'Mở rộng'}</span>
      </button>

      {open && (
        <div className="mt-4">
          {isLoading ? (
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Đang tải...</div>
          ) : error ? (
            <EsmsErrorBox error={error} isDark={isDark} compact />
          ) : !data ? (
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chưa có dữ liệu</div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>SMS CSKH</h4>
                <PriceTable rows={toRows(data.care)} isDark={isDark} />
              </div>
              <div>
                <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>SMS Quảng cáo</h4>
                <PriceTable rows={toRows(data.ads)} isDark={isDark} />
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                Cập nhật: {new Date(data.fetchedAt).toLocaleString('en-US')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
